import type { JSX, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { toast } from '@/lib/toast';
import {
  AlertTriangle,
  BadgeCheck,
  Check,
  ChevronDown,
  Download,
  Eye,
  FileText,
  Loader2,
  Mail,
  MoreHorizontal,
  Pencil,
  Trash2,
} from 'lucide-react';
import { Button, StatusChip } from '@/components';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { useReportDetailContext } from '../ReportDetailContext';
import { useShellHeaderHeight } from '../hooks/useShellHeaderHeight';
import { useReportHeaderOffset } from '../hooks/useReportHeaderOffset';
import { ReportSideNav } from './ReportSideNav';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatIsoDateDDMMYYYY, formatIsoDateTimeItShort } from '@/utils/date';
import { resolvePlannedVisitAt } from '@/utils/inspectionScheduling';
import { useNavigate } from '@tanstack/react-router';
import { useUserRole } from '@/features/auth';

export function ReportDetailScaffold({ children }: { children: ReactNode }): JSX.Element {
  const {
    report,
    company,
    site,
    onPreviewPdf,
    onGeneratePdf,
    isPdfLoading,
    pdfError,
    onRetryPdf,
    onDismissPdfError,
    nav,
    isEditMode,
    onToggleEdit,
    isDraft,
    schedulingWarning,
    onApplyPlannedToReport,
    onApplyReportToPlanned,
    onFinalizeDraft,
    onDeleteDraft,
    isFinalizing,
    isDeletingDraft,
    lockMessage,
    lockState,
  } = useReportDetailContext();
  const navigate = useNavigate();
  const { canFinalizeReports } = useUserRole();

  const shellHeaderHeight = useShellHeaderHeight();
  const reportHeaderOffset = useReportHeaderOffset();
  // Sidebar sticks below the report header (which is at top:0 of scroll container)
  // reportHeaderOffset includes shell height, so we subtract it
  const reportHeaderHeight = Math.max(reportHeaderOffset - shellHeaderHeight, 0);
  const navTopOffset = reportHeaderHeight + 24;
  const navMaxHeight = `calc(100vh - ${navTopOffset + 32}px)`;

  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const siteName = report.meta.siteName ?? site?.name ?? '—';
  const visitDateLabel = report.meta.visitDate ? formatIsoDateDDMMYYYY(report.meta.visitDate) : '—';
  const sitePlannedVisitAt = site ? resolvePlannedVisitAt(site) : undefined;
  const plannedDateTime = report.plannedVisitAt ?? sitePlannedVisitAt;
  const plannedDateLabel = plannedDateTime ? formatIsoDateTimeItShort(plannedDateTime, '—') : '—';
  const reportDateLabel = report.reportAt ? formatIsoDateTimeItShort(report.reportAt, '—') : '—';
  const lockDateLabel = lockState?.lockedAt && formatIsoDateDDMMYYYY(lockState.lockedAt);

  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const recipientEmails = (() => {
    if (!company?.personnel?.length) return [];
    const siteId = site?.id;
    const emails = company.personnel
      .filter(
        (person) =>
          person.reportRecipient &&
          person.email &&
          (person.scope === 'company' || (person.scope === 'site' && person.siteId === siteId))
      )
      .map((person) => person.email?.trim())
      .filter((email): email is string => Boolean(email && email.length > 0));
    return Array.from(new Set(emails));
  })();

  const handleCopyEmails = async () => {
    if (recipientEmails.length === 0) {
      toast.error('Nessun destinatario configurato');
      return;
    }
    const payload = recipientEmails.join(', ');
    if (!navigator.clipboard) {
      toast.error('Copia non disponibile in questo browser');
      return;
    }
    try {
      await navigator.clipboard.writeText(payload);
      toast.success('Email copiate negli appunti');
    } catch (error) {
      console.error('Failed to copy emails:', error);
      toast.error('Errore durante la copia delle email');
    }
  };

  const handleViewActivities = () => {
    if (!report?.id) return;
    navigate({
      to: '/activities',
      search: {
        query: undefined,
        status: undefined,
        companyId: undefined,
        siteId: undefined,
        reportId: report.id,
      },
    });
  };

  return (
    <div className="flex h-full flex-col bg-neutral-50/50 -mx-6 -my-6">
      <div
        data-report-header
        className={cn(
          'sticky z-30 mx-6 mt-6 rounded-lg border border-[var(--border-default)] bg-surface/98 backdrop-blur transition-all',
          isScrolled && 'shadow-md'
        )}
        style={{ top: 8 }}
      >
        <div className="px-4 py-2">
          <div className="flex items-center justify-between gap-3">
            {/* Left: Context info - compact single line */}
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex items-center gap-2 min-w-0 text-sm">
                <span className="font-medium text-text-primary truncate" title={siteName}>
                  {siteName}
                </span>
                <span className="text-text-tertiary shrink-0">·</span>
                <span className="text-text-secondary shrink-0">{visitDateLabel}</span>
              </div>
              <StatusChip
                status={isDraft ? 'draft' : 'finalized'}
                showIcon
                className="h-5 px-2 text-[10px] font-semibold shrink-0"
              />
            </div>

            {/* Right: Actions - compact buttons */}
            <div className="flex items-center gap-2 shrink-0">
              {isDraft && (
                <Button
                  variant={isEditMode ? 'primary' : 'secondary'}
                  size="sm"
                  onClick={onToggleEdit}
                  className={cn(
                    'gap-1.5 transition-all',
                    isEditMode && 'ring-2 ring-brand-blue-300 ring-offset-1'
                  )}
                >
                  {isEditMode ? (
                    <Check className="h-3.5 w-3.5" />
                  ) : (
                    <Pencil className="h-3.5 w-3.5" />
                  )}
                  {isEditMode ? 'Termina modifica' : 'Modifica'}
                </Button>
              )}

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isPdfLoading}
                    className="gap-1.5"
                    aria-busy={isPdfLoading}
                  >
                    {isPdfLoading ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <FileText className="h-3.5 w-3.5" />
                    )}
                    <span>{isPdfLoading ? 'PDF...' : 'PDF'}</span>
                    {!isPdfLoading && <ChevronDown className="h-3.5 w-3.5" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={onPreviewPdf}>
                    <Eye className="mr-2 h-4 w-4" />
                    Anteprima
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={onGeneratePdf}>
                    <Download className="mr-2 h-4 w-4" />
                    Esporta
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-8 w-8"
                    aria-label="Azioni report"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {isDraft ? (
                    <>
                      {canFinalizeReports && (
                        <DropdownMenuItem
                          onClick={onFinalizeDraft}
                          disabled={isFinalizing || isDeletingDraft}
                        >
                          <BadgeCheck className="mr-2 h-4 w-4" />
                          Finalizza report
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={onDeleteDraft}
                        disabled={isFinalizing || isDeletingDraft}
                        destructive
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Elimina bozza
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  ) : null}
                  <DropdownMenuItem onClick={handleViewActivities}>
                    <AlertTriangle className="mr-2 h-4 w-4" />
                    Vedi attività collegate
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleCopyEmails}
                    disabled={recipientEmails.length === 0}
                    title={
                      recipientEmails.length === 0 ? 'Nessun destinatario configurato' : undefined
                    }
                  >
                    <Mail className="mr-2 h-4 w-4" />
                    Copia email
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* PDF error alert - below the main row */}
          {pdfError && (
            <Alert
              variant="destructive"
              className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
            >
              <div>
                <AlertTitle className="text-sm">Generazione PDF fallita</AlertTitle>
                <AlertDescription className="text-xs">Riprova a generare il PDF.</AlertDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={onRetryPdf}>
                  Riprova
                </Button>
                <Button size="sm" variant="ghost" onClick={onDismissPdfError}>
                  Chiudi
                </Button>
              </div>
            </Alert>
          )}
          {lockMessage ? (
            <div className="mt-2 flex items-center gap-2 rounded-md border border-warning-500 bg-warning-100 px-3 py-2 text-xs">
              <AlertTriangle className="h-3.5 w-3.5 text-brand-blue-800 shrink-0" />
              <p className="text-brand-blue-800">
                <span className="font-semibold">Modifica bloccata:</span> {lockMessage}
                {lockDateLabel ? ` Blocco attivo dal ${lockDateLabel}.` : ''}
              </p>
            </div>
          ) : null}
          {schedulingWarning?.isPlannedMismatch ? (
            <div className="mt-2 rounded-md border border-warning-500 bg-warning-100 px-3 py-2 text-xs text-brand-blue-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                <div className="flex-1 space-y-1.5">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="font-semibold">Pianificazione non allineata</span>
                    <span className="text-[10px] text-brand-blue-900/70">
                      Pianificato: {plannedDateLabel} · Report: {reportDateLabel}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={onApplyPlannedToReport}
                      disabled={!plannedDateTime || !onApplyPlannedToReport}
                    >
                      Applica pianificato al report
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={onApplyReportToPlanned}
                      disabled={!report.reportAt || !onApplyReportToPlanned}
                    >
                      Aggiorna pianificazione dal report
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="flex min-h-0 flex-1 gap-6 px-6 py-6">
        <aside
          className={cn(
            'w-64 shrink-0 transition-transform duration-200',
            'sticky self-start',
            'max-lg:fixed max-lg:inset-y-0 max-lg:left-0 max-lg:z-30 max-lg:w-72 max-lg:top-0 max-lg:max-h-screen',
            !mobileNavOpen && 'max-lg:-translate-x-full'
          )}
          style={{ top: navTopOffset, maxHeight: navMaxHeight }}
        >
          <div className="flex h-full flex-col max-lg:border-r max-lg:bg-surface max-lg:shadow-xl">
            <div className="h-full overflow-y-auto rounded-lg border border-[var(--border-default)] bg-surface shadow-sm">
              <ReportSideNav navigation={nav} onNavigate={() => setMobileNavOpen(false)} />
            </div>
          </div>
        </aside>

        <main className="flex-1 min-w-0">
          <div className="lg:hidden mb-4">
            <Button variant="secondary" size="sm" onClick={() => setMobileNavOpen((prev) => !prev)}>
              {mobileNavOpen ? 'Nascondi navigazione' : 'Mostra navigazione'}
            </Button>
          </div>

          <div className="pb-12">{children}</div>
        </main>
      </div>
    </div>
  );
}
